import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XOctagon } from "lucide-react";
import { Button } from "./Button";

export type ToastTone = "ok" | "crit" | "warn" | "info";

interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  body?: string;
  durationMs: number;
  leaving?: boolean;
}

interface ToastApi {
  show: (t: { tone?: ToastTone; title: string; body?: string; durationMs?: number }) => void;
  ok: (title: string, body?: string) => void;
  error: (title: string, body?: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);
const ICON = { ok: CheckCircle2, crit: XOctagon, warn: AlertTriangle, info: Info } as const;
/** Tempo da animação de saída (motion.css, vx-toast-out). */
const LEAVE_MS = 180;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const remove = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  // Sai com animação e só então deixa a lista.
  const dismiss = useCallback(
    (id: number) => {
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, leaving: true } : x)));
      window.setTimeout(() => remove(id), LEAVE_MS);
    },
    [remove],
  );

  const show = useCallback<ToastApi["show"]>(({ tone = "info", title, body, durationMs }) => {
    const id = ++seq.current;
    const ms = durationMs ?? (tone === "crit" ? 9000 : 5000);
    setItems((xs) => [...xs.slice(-3), { id, tone, title, body, durationMs: ms }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      ok: (title, body) => show({ tone: "ok", title, body }),
      error: (title, body) => show({ tone: "crit", title, body }),
    }),
    [show],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="vx-toasts" aria-live="polite" aria-relevant="additions">
        {items.map((t) => (
          <Toast key={t.id} item={t} onDismiss={dismiss} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

/**
 * Um aviso: some sozinho no tempo dele, com a barra fina mostrando o que falta. Com o mouse em cima (ou o foco do
 * teclado dentro), o tempo para; ao sair, continua de onde parou.
 */
function Toast({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const [paused, setPaused] = useState(false);
  const remaining = useRef(item.durationMs);
  const startedAt = useRef(0);

  useEffect(() => {
    if (paused || item.leaving) return;
    startedAt.current = Date.now();
    const t = window.setTimeout(() => onDismiss(item.id), remaining.current);
    return () => {
      window.clearTimeout(t);
      remaining.current = Math.max(0, remaining.current - (Date.now() - startedAt.current));
    };
  }, [paused, item.leaving, item.id, onDismiss]);

  const Icon = ICON[item.tone];
  return (
    <div
      className={`vx-toast vx-toast--${item.tone}`}
      role={item.tone === "crit" ? "alert" : "status"}
      data-leaving={item.leaving ? "" : undefined}
      data-paused={paused ? "" : undefined}
      style={{ "--vx-toast-ms": `${item.durationMs}ms` } as CSSProperties}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <Icon className="vx-toast__icon" aria-hidden="true" />
      <div className="vx-toast__body">
        <p className="vx-toast__title">{item.title}</p>
        {item.body ? <p className="text-2">{item.body}</p> : null}
      </div>
      <Button variant="ghost" size="sm" iconOnly label="Dispensar aviso" icon={<X />} onClick={() => onDismiss(item.id)} />
      <span className="vx-toast__timer" aria-hidden="true" />
    </div>
  );
}

export function useToast(): ToastApi {
  const api = useContext(Ctx);
  if (!api) throw new Error("useToast fora de <ToastProvider>");
  return api;
}
